import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { segmentCase, useIsMobile } from '@guildeducationinc/catalog-tools';
import { Button } from '@guildeducationinc/recess';
import ExpandMore from '@guildeducationinc/recess/icons/ExpandMoreIcon';
import { trackEvent } from 'src/services/analytics';
import styles from './ShowMore.module.scss';

/**
 * This is a generic implementation for the show more/less
 * functionality. There is a custom implementation for the
 * Institution section of program details. There is a custom implementation for the
 * Concentratrions component due to accessilbility.
 */

interface Props extends React.HTMLAttributes<HTMLDivElement> {
	children: React.ReactElement;
	section: string;
}

export const ShowMore: React.FC<Props> = ({ children, section }) => {
	const [showMore, setShowMore] = useState<boolean>(false);
	const isMobile = useIsMobile();
	const characterLimit = 500;
	const mobileCharacterLimit = 300;
	const isTextOnly = useMemo(() => {
		return (children && children.props && typeof children.props.children === 'string') || false;
	}, [children]);

	const isOverMinChildrenLength = useMemo(() => {
		return (
			(children && children.props && children.props.children.length > 3) ||
			(Array.isArray(children) && children?.length > 3)
		);
	}, [children]);

	const isChildrenArray = useMemo(() => {
		return !isTextOnly && Array.isArray(children);
	}, [children, isTextOnly]);

	const isChildrenObject = useMemo(() => {
		return !isTextOnly && !Array.isArray(children);
	}, [children, isTextOnly]);

	const hideShowMoreButton = useMemo(() => {
		return (
			(!isTextOnly && !isOverMinChildrenLength) ||
			(isTextOnly && children.props.children.length < characterLimit) ||
			(isChildrenArray && Array.isArray(children) && children?.length <= 3)
		);
	}, [isTextOnly, isOverMinChildrenLength, children, isChildrenArray]);

	const toggleShowMore = useCallback(() => {
		const trackButtonClick = () => {
			const buttonText = showMore ? 'show_less' : 'show_more';
			trackEvent('Clickable', {
				tag: `${section}_${buttonText}`,
				text: `${segmentCase(buttonText)}`,
				type: 'button',
			});
		};

		trackButtonClick();
		setShowMore(!showMore);
	}, [section, showMore]);

	return (
		<>
			{isTextOnly && (
				<div
					className={classNames({
						[styles.collapse]: !showMore && children.props.children.length > characterLimit,
					})}
					data-testid='text-only'
				>
					{showMore
						? children.props.children
						: children.props.children.substring(
								0,
								isMobile ? mobileCharacterLimit : characterLimit
							)}
				</div>
			)}
			{isChildrenObject && (
				<div
					className={classNames({
						[styles.collapse]: !showMore && isOverMinChildrenLength,
					})}
					data-testid='object-list-items'
					role='list'
				>
					{isOverMinChildrenLength && !showMore
						? children?.props.children.slice(0, 3)
						: children?.props?.children}
				</div>
			)}
			{isChildrenArray && (
				<div
					className={classNames({
						[styles.collapse]: !showMore && Array.isArray(children) && children?.length > 3,
					})}
					data-testid='array-list-items'
				>
					{isOverMinChildrenLength && !showMore
						? Array.isArray(children) && children?.slice(0, 3)
						: children}
				</div>
			)}

			<div>
				<Button
					appearance='ghost'
					aria-expanded={showMore}
					className={hideShowMoreButton ? styles.hide : styles.showMoreBtn}
					icon={
						<ExpandMore className={classNames({ [styles.showMoreIcon]: showMore })} size={24} />
					}
					iconDirection='right'
					onClick={toggleShowMore}
					testid='show-more-button'
				>
					{showMore ? 'Show less' : 'Show more'}
				</Button>
			</div>
		</>
	);
};
